/**
 * Ec2Card — full detail card for EC2 instance in Resource Grid view.
 *
 * Shows health badge, instance ID, type, state, IP, and metric gauges
 * for CPU, Memory, and Heap (where available).
 */
import type { Ec2Instance } from '@/types';
import { StatusBadge } from '../primitives/StatusBadge';
import { MetricGauge } from '../primitives/MetricGauge';
import styles from './Ec2Card.module.css';

export interface Ec2CardProps {
  instance: Ec2Instance;
}

export function Ec2Card({ instance }: Ec2CardProps) {
  const heapPercent = instance.jvmMetrics?.heapUtilizationPercent ?? null;

  return (
    <article
      className={styles.card}
      aria-label={`EC2 instance ${instance.resourceId}`}
    >
      <header className={styles.header}>
        <StatusBadge status={instance.healthStatus} size="md" />
        <span className={styles.badge}>EC2</span>
      </header>

      <div className={styles.body}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Instance ID</span>
          <span className={styles.fieldValue} title={instance.resourceId}>
            {instance.resourceId}
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Type</span>
          <span className={styles.fieldValue}>{instance.instanceType}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>State</span>
          <span className={styles.fieldValue}>{instance.state}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>AZ</span>
          <span className={styles.fieldValue}>{instance.availabilityZone}</span>
        </div>
        {instance.privateIp && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Private IP</span>
            <span className={styles.fieldValue}>{instance.privateIp}</span>
          </div>
        )}
        {instance.gitBranch && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Git branch</span>
            <span className={styles.fieldValue}>{instance.gitBranch}</span>
          </div>
        )}
      </div>

      <div className={styles.gauges}>
        <MetricGauge label="CPU" value={instance.cpuUtilization} warnThreshold={70} critThreshold={90} />
        <MetricGauge label="Memory" value={instance.memoryUtilization} warnThreshold={80} critThreshold={95} />
        {heapPercent !== null && (
          <MetricGauge label="Heap" value={heapPercent} warnThreshold={75} critThreshold={90} />
        )}
      </div>

      {instance.healthProbe && (
        <div className={styles.probe}>
          <span className={styles.fieldLabel}>Health probe</span>
          <div className={styles.probeRow}>
            <StatusBadge status={instance.healthProbe.healthStatus} size="sm" />
            <span className={styles.probeUrl}>{instance.healthProbe.targetUrl}</span>
          </div>
        </div>
      )}
    </article>
  );
}
